import { useIdleTimer } from "react-idle-timer";
import Toast from "./Toast";
// import SHA512 from "crypto-js/sha512";

// const generateStringHash = (value: string): string => SHA512(value).toString();

const toastProps = { theme: "colored" };

const getInitial = (name = ""): any =>
  name
    .replace(/\s+/, " ")
    .split(" ")
    .slice(0, 2)
    .map((v) => v && v[0].toUpperCase())
    .join("");

const handleErrorResponse = (error: any): void => {
  if (error && error.response) {
    const { data, status } = error.response;
    if (status === 400 || status === 412 || status === 403 || status === 409) {
      const { msg } = data;
      Toast.showErrorMessage(msg);
    } else if (status === 500) {
      Toast.showErrorMessage("Please try again");
    }
  }
};

const handleSuccessResponse = (response: any): any => {
  const { msg } = response.data;
  Toast.showSuccessMessage(msg);
};

const handleMessageResponse = (msg) => {
  Toast.showInfoMessage(msg, toastProps);
};

const titleCase = (value: string): any => {
  if (value) {
    return value
      .trim()
      .split(" ")
      .map((w) =>
        w !== "" ? w[0].toUpperCase() + w.substring(1).toLowerCase() : "",
      )
      .join(" ");
  }
  return "";
};

const fileExtension = (fileName: string): string => {
  if (fileName) {
    const url = fileName.split("?")[0];
    const extension = url.split(".").pop();
    return extension || "";
  }
  return "";
};

const isPDFDocument = (documentUrl: string): boolean =>
  fileExtension(documentUrl) === "pdf";

const getFileType = (file: any): any =>
  file.type === "application/pdf" ? "pdf" : "image";

const getFileFromBlobUrl = (blobUrl: any): Promise<any> =>
  fetch(blobUrl).then((r) => r.blob());

const convertBase64ToFile = (base64: string): Promise<any> =>
  new Promise((resolve, reject) => {
    fetch(base64)
      .then((res) => res.blob())
      .then((blob) => {
        const file = new File([blob], "sign.png", {
          type: "image/png",
        });
        resolve(file);
      })
      .catch((error) => {
        reject(error);
      });
  });

const convertFileToBase64 = (file: any): Promise<any> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      resolve(reader.result);
    };
    reader.onerror = (error) => {
      reject(error);
    };
  });

const convertBase64SVGToBase64Jpeg = (
  svgReference: string,
  base64: string,
): Promise<any> =>
  new Promise((resolve, reject) => {
    try {
      const svgNode: any = document.getElementById(svgReference);
      const width = svgNode.width.baseVal.value * 3;
      const height = svgNode.height.baseVal.value * 3;
      const image = new Image();
      image.onload = () => {
        const canvas: any = document.getElementById("svg-canvas");
        const context: any = canvas.getContext("2d");
        canvas.width = width;
        canvas.height = height;
        context.clearRect(0, 0, width, height);
        context.drawImage(image, 0, 0, width, height);
        const imageUrl = canvas.toDataURL();
        resolve(imageUrl);
      };
      image.src = base64;
    } catch (error) {
      reject(error);
    }
  });

const convertSVGToImage = (svgReference: string): string => {
  const svgNode: any = document.getElementById(svgReference);
  const s = new XMLSerializer().serializeToString(svgNode);
  return `data:image/svg+xml;base64,${decodeURI(encodeURIComponent(s))})}`;
};

const appendBlobURL = (files: File[]): File[] =>
  files.map((file) =>
    Object.assign(file, {
      preview: URL.createObjectURL(file),
    }),
  );

const getFileTypeFromUrl = (url: any): any => {
  const baseUrl = url.split("?")[0];
  const extension = baseUrl.split(".").pop();
  return extension === "pdf" ? "pdf" : "image";
};

const getFileName = (fileName: any): any => fileName.split(".")[0];

// const generateStringHash = (value: string): string =>
//     createHash('sha512').update(value).digest('hex');

const truncateLast = (str: any): string => {
  if (str.length > 30) {
    return `${str.substr(0, 30)}...`;
  }
  return str;
};

const truncateMiddle = (str: any): any => {
  if (str.length > 35) {
    return `${str.substr(0, 15)}...${str.substr(str.length - 15, str.length)}`;
  }
  return str;
};

const fileNameFromURL = (fileURL: string): string => {
  const url = fileURL.split("?")[0];
  const fileName = url.split("/").pop();
  return fileName !== undefined ? fileName : "Default.pdf";
};

export const DefaultPagination = (limit: any): any => ({
  offset: 1,
  lastOffset: 0,
  limit,
  total: 0,
});

const formatCurrencyWithDecimal = (value: any): string => {
  console.log(value);
  if (!value && value !== 0) return "";

  let numericValue: string;
  if (typeof value === "number") {
    numericValue = value.toString();
  } else {
    numericValue = value.replace(/[^\d.-]/g, "");
  }

  if (numericValue === "" || numericValue === "." || numericValue === "-") {
    return numericValue;
  }

  if (numericValue.endsWith(".")) {
    const number = parseFloat(numericValue.slice(0, -1));
    if (isNaN(number)) return "";
    return number.toString() + ".";
  }

  const number = parseFloat(numericValue);
  if (isNaN(number)) return "";

  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(number);
};

const formatCurrency = (value: any): string => {
  console.log(value);
  if (!value && value !== 0) return "";

  let numericValue: string;
  if (typeof value === "number") {
    numericValue = value.toString();
  } else {
    numericValue = value.replace(/[^\d.]/g, "");
  }
  const number = parseFloat(numericValue);

  if (isNaN(number)) return "";

  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(number);
};

export const useAppIdleTimer = (onIdle, timeout = 60000) => {
  const idleTimer = useIdleTimer({
    timeout,
    onIdle,
    debounce: 500,
  });

  return idleTimer;
};

export default {
  handleMessageResponse,
  handleSuccessResponse,
  handleErrorResponse,
  titleCase,
  getInitial,
  fileExtension,
  isPDFDocument,
  getFileFromBlobUrl,
  getFileType,
  convertBase64ToFile,
  convertSVGToImage,
  convertBase64SVGToBase64Jpeg,
  convertFileToBase64,
  appendBlobURL,
  getFileTypeFromUrl,
  getFileName,
  // generateStringHash,
  truncateLast,
  truncateMiddle,
  fileNameFromURL,
  DefaultPagination,
  formatCurrency,
  formatCurrencyWithDecimal,
};
